import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import {
  ClipboardList, Users, BarChart2, DollarSign, AlertTriangle,
  LogOut, Bell, Menu, X, Sun, Moon, Target, Briefcase, ChevronDown
} from 'lucide-react';
import { getTmNotifications, markTmNotificationRead, markAllTmNotificationsRead } from '../../utils/tm_api';
import { getSidebarStats } from '../../utils/api';



/**
 * Role-based nav items for TM Layout
 * roles: array of roles that can see this item (empty = all)
 */
const tmNavItems = [
  { to: '/tm/tasks', icon: ClipboardList, label: 'المهام', roles: [] },
  { to: '/tm/teams', icon: Users, label: 'الفرق والأعضاء', roles: ['owner', 'admin', 'supervisor', 'team_leader'] },
  { to: '/tm/supervisors', icon: Target, label: 'المتابعين', roles: ['owner', 'admin'] },
  { to: '/tm/earnings', icon: DollarSign, label: 'المستحقات', roles: [] },
  { to: '/tm/dashboard', icon: BarChart2, label: 'أداء الفرق', roles: ['owner', 'admin', 'supervisor', 'team_leader'] },
  { to: '/tm/complaints', icon: AlertTriangle, label: 'الشكاوى', roles: ['owner', 'admin', 'team_leader', 'team_member'] },
];

// Role display labels
const roleLabels = {
  owner: 'المالك',
  admin: 'مدير',
  supervisor: 'متابع',
  team_leader: 'قائد فريق',
  team_member: 'عضو فريق',
  employee: 'موظف',
  finance: 'مالية',
  ops: 'عمليات',
};

export default function TmLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [personalNotifs, setPersonalNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBell, setShowBell] = useState(false);
  const [menuOpen, setMenuOpen] = useState(true);
  const [sidebarStats, setSidebarStats] = useState({ unpaid_invoices_count: 0, active_tasks_count: 0, open_complaints_count: 0 });
  const bellRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('tm_login_source');
    logout();
    navigate('/team-login');
  };

  const loadSidebarStats = async () => {
    try {
      const stats = await getSidebarStats();
      setSidebarStats(stats);
    } catch (e) {
      console.error('[sidebar] Failed to fetch stats:', e);
    }
  };

  const loadPersonalNotifs = async () => {
    try {
      const data = await getTmNotifications(20);
      setPersonalNotifs(Array.isArray(data) ? data : []);
      setUnreadCount(Array.isArray(data) ? data.filter(n => !n.is_read).length : 0);
    } catch (e) { /* silently ignore */ }
  };

  useEffect(() => {
    loadPersonalNotifs();
    loadSidebarStats();
    const timer = setInterval(() => {
      loadPersonalNotifs();
      loadSidebarStats();
    }, 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!showBell) return;
    const handler = e => { if (bellRef.current && !bellRef.current.contains(e.target)) setShowBell(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showBell]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Filter nav items by user role
  const userRole = user?.role || '';
  const visibleNavItems = tmNavItems.filter(item => {
    if (item.roles.length === 0) return true;
    return item.roles.includes(userRole);
  });

  // Customize labels per role
  const getLabel = (item) => {
    if (item.to === '/tm/tasks') {
      if (userRole === 'team_member') return 'مهامي';
      if (userRole === 'team_leader') return 'مهام فريقي';
      return item.label;
    }
    if (item.to === '/tm/teams') {
      if (userRole === 'team_leader') return 'فريقي';
      return item.label;
    }
    if (item.to === '/tm/earnings') {
      if (userRole === 'team_member') return 'مستحقاتي';
      if (userRole === 'team_leader') return 'مستحقات الفريق';
      if (userRole === 'supervisor') return 'عمولاتي';
      return item.label;
    }
    if (item.to === '/tm/dashboard') {
      if (userRole === 'supervisor') return 'العملاء';
      return item.label;
    }
    if (item.to === '/tm/complaints') {
      if (userRole === 'team_leader' || userRole === 'team_member') return 'رفع شكوى';
      return item.label;
    }
    return item.label;
  };

  const SidebarContent = () => {
    const groupBadgeCount = (sidebarStats.active_tasks_count || 0) + (sidebarStats.open_complaints_count || 0);
    const isGroupActive = visibleNavItems.some(c => location.pathname === c.to);

    return (
      <>
        {/* Logo */}
        <div className="p-6 border-b border-white/5 shrink-0 bg-white/[0.02]">
          <div className="flex items-center gap-3 text-right" dir="rtl">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/15 shrink-0">
              <Users size={20} className="text-white" />
            </div>
            <div>
              <div className="text-base font-black leading-tight tracking-widest">
                <span className="text-white">MS </span>
                <span className="text-primary font-black">MEDIA</span>
              </div>
              <div className="text-gray-400 text-xs mt-0.5">إدارة الفرق والمهمات</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto custom-scrollbar">
          <div className="px-3 space-y-1">
            {/* Main Collapsible Group */}
            <div className="mt-2">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                  isGroupActive
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                }`}
              >
                <Users size={18} className="shrink-0" />
                <span className="flex-1 text-right">إدارة الفرق</span>
                {groupBadgeCount > 0 && !menuOpen && (
                  <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 ml-1 block animate-pulse" />
                )}
                <ChevronDown size={14} className={`shrink-0 transition-transform duration-300 ${menuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                menuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="mr-5 pr-2 space-y-0.5 py-1 relative">
                  {/* Vertical line indicator */}
                  <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-primary/30 via-white/10 to-transparent" />
                  {visibleNavItems.map(item => {
                    let Icon = item.icon;
                    if (item.to === '/tm/dashboard' && userRole === 'supervisor') {
                      Icon = Briefcase;
                    }
                    const isChildActive = location.pathname === item.to;
                    const badgeKey = item.to === '/tm/tasks' ? 'active_tasks_count' : item.to === '/tm/complaints' ? 'open_complaints_count' : null;
                    const badgeValue = badgeKey ? sidebarStats[badgeKey] : 0;
                    return (
                      <NavLink key={item.to} to={item.to} className={({ isActive }) => `
                        flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs transition-all duration-200 relative
                        ${isActive
                          ? 'bg-gradient-to-r from-primary/15 to-blue-500/5 text-white font-semibold shadow-inner'
                          : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                        }
                      `}>
                        {/* Horizontal connection dot */}
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 absolute -right-[11.5px] transition-all duration-300 ${
                          isChildActive ? 'bg-primary scale-125 shadow-[0_0_8px_var(--primary)]' : 'bg-white/20'
                        }`} />
                        <Icon size={14} className="shrink-0" />
                        <span className="flex-1 text-right">{getLabel(item)}</span>
                        {badgeValue > 0 && (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            badgeKey === 'open_complaints_count' 
                              ? 'bg-red-500 text-white animate-pulse' 
                              : 'bg-blue-500 text-white'
                          }`}>
                            {badgeValue}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* User */}
        <div className="p-4 border-t border-white/5 shrink-0 bg-white/[0.01]">
          <div className="flex items-center gap-3 mb-4 bg-white/5 p-3 rounded-xl border border-white/5 relative group transition-all duration-300 hover:bg-white/[0.08]" dir="rtl">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-sm shrink-0 uppercase shadow-md relative">
              {user?.name?.[0] || 'M'}
              {/* Live session active dot */}
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-slate-900 animate-pulse" />
            </div>
            <div className="overflow-hidden text-right">
              <div className="text-white text-sm font-semibold truncate group-hover:text-primary transition-colors duration-200">
                {user?.name || user?.username}
              </div>
              <div className="text-gray-400 text-xs">
                {roleLabels[user?.role] || user?.role}
              </div>
            </div>
          </div>

          {/* Link to main system (Owner/Admin only) */}
          {(userRole === 'owner' || userRole === 'admin') && (
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-gray-400 hover:text-primary hover:bg-white/5 transition-all duration-200 mb-2 text-xs font-semibold"
            >
              ← العودة للنظام الرئيسي
            </button>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-gray-400 text-sm font-medium hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
          >
            <LogOut size={16} />
            تسجيل الخروج
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-deeper text-dark dark:text-gray-100 overflow-hidden selection:bg-primary/20 transition-colors duration-200">
      {/* ── Mobile Sidebar Overlay ── */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`
        fixed inset-y-0 right-0 z-50 w-72 bg-gradient-to-b from-[#0F1E33] via-[#091220] to-[#040810] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out border-l border-white/5
        lg:translate-x-0 lg:static lg:shrink-0
        ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <SidebarContent />
      </aside>

      {/* ── Main Content ── */}
      <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative">
        {/* Topbar */}
        <header className="h-16 bg-white/80 dark:bg-dark-deeper/80 backdrop-blur-md border-b border-gray-200 dark:border-white/10 flex items-center justify-between px-4 lg:px-8 shrink-0 z-30 sticky top-0 transition-colors duration-200">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 -mr-2 text-gray-500 hover:text-dark dark:hover:text-gray-100 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors" onClick={() => setMobileMenuOpen(true)}>
              <Menu size={24} />
            </button>
            <div className="text-dark dark:text-white font-bold text-lg hidden sm:flex items-center gap-2">
              <span className="font-black tracking-wider">MS </span><span className="text-primary-dark font-black tracking-wider">MEDIA</span>
              <span className="text-gray-400 font-normal mx-2">|</span>
              <span className="text-gray-400 font-normal">إدارة الفرق</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="w-10 h-10 rounded-xl border border-gray-200 dark:border-white/10 dark:bg-white/5 bg-white dark:bg-dark-deeper flex items-center justify-center text-gray-500 dark:text-gray-400 transition-all duration-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div ref={bellRef} className="relative">
              <button
                onClick={() => setShowBell(v => !v)}
                className={`w-10 h-10 rounded-xl border flex items-center justify-center relative transition-all duration-200 ${showBell ? 'bg-gray-50 border-gray-300 dark:bg-white/10 dark:border-white/20' : 'bg-white border-gray-200 dark:bg-white/5 dark:border-white/10'}`}
              >
                <Bell size={18} className={unreadCount > 0 ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500'} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -left-1 min-w-[18px] h-[18px] rounded-full bg-blue-500 ring-2 ring-white dark:ring-dark-deeper text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showBell && (
                <div className="absolute top-[calc(100%+8px)] left-0 w-80 bg-white dark:bg-dark-deeper rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100/50 dark:border-white/10 z-50 overflow-hidden transform origin-top-left transition-all">
                  <div className="px-5 py-4 border-b border-gray-50 dark:border-white/5 flex items-center justify-between bg-gray-50/50 dark:bg-white/5">
                    <span className="font-bold text-dark dark:text-white text-sm flex items-center gap-2">
                      التنبيهات
                      {unreadCount > 0 && <span className="bg-blue-500/20 text-blue-550 dark:text-blue-400 text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>}
                    </span>
                    {unreadCount > 0 && (
                      <button onClick={async () => { await markAllTmNotificationsRead(); loadPersonalNotifs(); }} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">قراءة الكل</button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto custom-scrollbar">
                    {personalNotifs.filter(n => !n.is_read).map(n => (
                      <div key={n.id} className="p-3 border-b border-gray-100 dark:border-white/[0.04] hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors cursor-pointer flex items-start gap-3" onClick={async () => { await markTmNotificationRead(n.id); loadPersonalNotifs(); }}>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-blue-500/10 text-blue-500 dark:text-blue-400 text-xs">📋</div>
                        <div className="flex-1">
                          <div className="text-xs font-bold text-gray-900 dark:text-gray-100">{n.title}</div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{n.message}</div>
                        </div>
                      </div>
                    ))}
                    {personalNotifs.filter(n => !n.is_read).length === 0 && (
                      <div className="p-6 text-center text-gray-400 dark:text-gray-500 text-sm">✅ لا توجد تنبيهات جديدة</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Dynamic Page Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8 custom-scrollbar relative z-10 w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
